import React, { useState, useEffect, useCallback } from 'react';
import { Package, Clock, AlertCircle, CheckCircle, ChevronRight, FileText, CheckSquare } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { quoteService } from '../services/quoteService';
import { questionnaireService } from '../services/questionnaireService';
import type { Quote } from '../types/quotes';
import type { QuestionnaireAssignment, QuestionnaireStatus } from '../types/questionnaires';
import CustomerSidebar from './CustomerSidebar';

type Section = 'angebote' | 'fragebogen' | 'aufgaben';

const CustomerDashboard: React.FC = () => {
  const { user } = useAuth();
  const [currentSection, setCurrentSection] = useState<Section>('angebote');
  const [orders, setOrders] = useState<Quote[]>([]);
  const [assignments, setAssignments] = useState<QuestionnaireAssignment[]>([]);
  const [loading, setLoading] = useState(true);

  const loadOrders = useCallback(async () => {
    if (!user?.uid || !user?.email) return;
    
    try {
      // Get quotes created by user OR where user is the customer
      const myOrders = await quoteService.getCustomerQuotes(user.uid, user.email);
      setOrders(myOrders);
      console.log('Loaded orders:', myOrders.length);
    } catch (error) {
      console.error('Error loading orders:', error);
    }
  }, [user?.uid, user?.email]);

  const loadAssignments = useCallback(async () => {
    if (!user?.email) return;
    
    try {
      const myAssignments = await questionnaireService.getAssignmentsForUser(user.email);
      setAssignments(myAssignments);
      console.log('Loaded assignments:', myAssignments.length);
    } catch (error) {
      console.error('Error loading assignments:', error);
    }
  }, [user?.email]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([loadOrders(), loadAssignments()]);
      setLoading(false);
    };
    loadData();
  }, [loadOrders, loadAssignments]);

  const getStatusBadge = (status: Quote['status']) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800',
      submitted: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    
    const labels = {
      draft: 'Entwurf',
      submitted: 'Gesendet',
      in_progress: 'In Bearbeitung',
      completed: 'Abgeschlossen',
      cancelled: 'Storniert'
    };

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const getFragebogenStatus = (order: Quote) => {
    if (order.fragebogenSubmitted) {
      return (
        <div className="flex items-center gap-2 text-green-600 text-sm">
          <CheckCircle className="w-4 h-4" />
          <span>Eingereicht</span>
        </div>
      );
    }
    
    return (
      <div className="flex items-center gap-2 text-orange-600 text-sm">
        <AlertCircle className="w-4 h-4" />
        <span>Ausstehend</span>
      </div>
    );
  };

  const getAssignmentStatusBadge = (assignment: QuestionnaireAssignment) => {
    const now = new Date();
    const deadline = new Date(assignment.deadline);
    const isOverdue = now > deadline && assignment.status !== 'submitted' && assignment.status !== 'cancelled';

    const styles: Record<QuestionnaireStatus | 'overdue', string> = {
      draft: 'bg-gray-100 text-gray-800',
      assigned: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      submitted: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };

    const labels: Record<QuestionnaireStatus | 'overdue', string> = {
      draft: 'Entwurf',
      assigned: 'Zugewiesen',
      in_progress: 'In Bearbeitung',
      submitted: 'Eingereicht',
      overdue: 'Überfällig',
      cancelled: 'Storniert'
    };

    const status = isOverdue ? 'overdue' : assignment.status;

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  if (loading) {
    return (
      <>
        <CustomerSidebar currentSection={currentSection} onSectionChange={setCurrentSection} />
        <div className="ml-64 flex items-center justify-center min-h-screen">
          <p className="text-gray-600">Lädt...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <CustomerSidebar currentSection={currentSection} onSectionChange={setCurrentSection} />
      
      <div className="ml-64 p-6">
        {/* Angebote Section */}
        {currentSection === 'angebote' && (
          <div className="max-w-6xl mx-auto">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Meine Angebote
              </h1>
              <p className="text-gray-600">
                Alle deine Webentwicklungs-Projekte auf einen Blick
              </p>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Gesamt</p>
                    <p className="text-3xl font-bold text-gray-900">{orders.length}</p>
                  </div>
                  <Package className="w-12 h-12 text-blue-600 opacity-20" />
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">In Bearbeitung</p>
                    <p className="text-3xl font-bold text-yellow-600">
                      {orders.filter(o => o.status === 'in_progress').length}
                    </p>
                  </div>
                  <Clock className="w-12 h-12 text-yellow-600 opacity-20" />
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Fragebogen offen</p>
                    <p className="text-3xl font-bold text-orange-600">
                      {orders.filter(o => !o.fragebogenSubmitted && o.status !== 'cancelled').length}
                    </p>
                  </div>
                  <AlertCircle className="w-12 h-12 text-orange-600 opacity-20" />
                </div>
              </div>
            </div>

            {/* Orders List */}
            {orders.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Noch keine Angebote
                </h3>
                <p className="text-gray-600 mb-6">
                  Erstelle dein erstes Projekt im Kalkulator!
                </p>
                <button
                  onClick={() => window.location.href = '/'}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  Zum Kalkulator
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <div
                    key={order.id}
                    className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-semibold text-gray-900">
                            {order.projectName || order.clientName || 'Webprojekt'}
                          </h3>
                          {getStatusBadge(order.status)}
                        </div>
                        <p className="text-sm text-gray-600">
                          Erstellt am {order.createdAt.toLocaleDateString('de-DE')}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-gray-900">
                          €{order.amountTotal.toFixed(2)}
                        </p>
                        <p className="text-sm text-gray-600">
                          {order.totalHours}h
                        </p>
                      </div>
                    </div>

                    {/* Fragebogen Status */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">
                          Kick-Off Fragebogen:
                        </p>
                        {getFragebogenStatus(order)}
                      </div>

                      <div className="flex gap-2">
                        {!order.fragebogenSubmitted && order.status !== 'cancelled' && (
                          <button
                            onClick={() => window.location.href = `/kickoff?quoteId=${order.id}`}
                            className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors flex items-center gap-2"
                          >
                            Fragebogen ausfüllen
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => window.location.href = `/my-orders/${order.id}`}
                          className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-200 transition-colors"
                        >
                          Details
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Fragebogen Section */}
        {currentSection === 'fragebogen' && (
          <div className="max-w-6xl mx-auto">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Kick-Off Fragebogen
              </h1>
              <p className="text-gray-600">
                Fragebogen für deine Webprojekte
              </p>
            </div>

            <div className="space-y-4">
              {orders
                .filter(o => o.status !== 'cancelled')
                .map((order) => (
                  <div
                    key={order.id}
                    className="bg-white rounded-lg shadow-md p-6"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {order.projectName || order.clientName || 'Webprojekt'}
                        </h3>
                        {getFragebogenStatus(order)}
                        {order.fragebogenSubmitted && order.fragebogenSubmittedAt && (
                          <p className="text-sm text-gray-600 mt-2">
                            Eingereicht am {order.fragebogenSubmittedAt.toLocaleDateString('de-DE')}
                          </p>
                        )}
                      </div>
                      {!order.fragebogenSubmitted && (
                        <button
                          onClick={() => window.location.href = `/kickoff?quoteId=${order.id}`}
                          className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center gap-2"
                        >
                          Jetzt ausfüllen
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}

              {orders.filter(o => o.status !== 'cancelled').length === 0 && (
                <div className="bg-white rounded-lg shadow-md p-12 text-center">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Keine Fragebogen vorhanden
                  </h3>
                  <p className="text-gray-600">
                    Erstelle erst ein Angebot, um den Kick-Off Fragebogen auszufüllen.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Aufgaben Section */}
        {currentSection === 'aufgaben' && (
          <div className="max-w-6xl mx-auto">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Meine Aufgaben
              </h1>
              <p className="text-gray-600">
                Dir zugewiesene Fragebögen
              </p>
            </div>

            {assignments.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <CheckSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Keine Aufgaben zugewiesen
                </h3>
                <p className="text-gray-600">
                  Du hast aktuell keine offenen Fragebogen-Aufgaben.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {assignments.map((assignment) => {
                  const deadline = new Date(assignment.deadline);
                  const isOverdue = new Date() > deadline && assignment.status !== 'submitted';

                  return (
                    <div
                      key={assignment.id}
                      className={`bg-white rounded-lg shadow-md p-6 ${
                        isOverdue ? 'border-l-4 border-red-500' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {assignment.questionnaireTitle}
                            </h3>
                            {getAssignmentStatusBadge(assignment)}
                          </div>
                          <p className="text-sm text-gray-600 mb-2">
                            {assignment.templateName}
                          </p>
                          <p className={`text-sm font-medium ${
                            isOverdue ? 'text-red-600' : 'text-gray-700'
                          }`}>
                            Fällig: {deadline.toLocaleDateString('de-DE')}
                          </p>
                        </div>
                        {assignment.status !== 'submitted' && assignment.status !== 'cancelled' && (
                          <button
                            onClick={() => window.location.href = `/questionnaire/${assignment.id}`}
                            className={`px-6 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2 ${
                              isOverdue
                                ? 'bg-red-600 hover:bg-red-700 text-white'
                                : 'bg-blue-600 hover:bg-blue-700 text-white'
                            }`}
                          >
                            {isOverdue ? 'Jetzt ausfüllen!' : 'Ausfüllen'}
                            <ChevronRight className="w-5 h-5" />
                          </button>
                        )}
                        {assignment.status === 'submitted' && assignment.submittedAt && (
                          <div className="text-right text-sm text-gray-500">
                            Eingereicht am{' '}
                            {assignment.submittedAt.toLocaleDateString('de-DE')}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default CustomerDashboard;
